import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  TableCellsIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { campaignService } from '../../services/campaignService';
import { adminService } from '../../services/adminService';
import { scriptService } from '../../services/scriptService';
import { BOT_ENGINES, CaptureField, PhoneNumber, ConversationStyle, conversationStylesVoiceError } from '../../types/campaign';
import { AdminUser } from '../../types/admin';
import { Script } from '../../types/script';


import SmsCampaignSetupFields from '../../components/Campaign/SmsCampaignSetupFields';
import DialScheduleFields from '../../components/Campaign/DialScheduleFields';
import { ConversationStylesManager } from '../../components/Campaign/ConversationStylesManager';
import PlaceholderHelper from '../../components/Campaign/PlaceholderHelper';
import TelnyxAssistantPicker from '../../components/Campaign/TelnyxAssistantPicker';
import PhoneNumberList from '../../components/Campaign/PhoneNumberList';
import Button from '../../components/UI/Button';
import Switch from '../../components/UI/Switch';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import { SectionHeader } from '../../components/UI/SectionHeader';
import { toast } from 'react-hot-toast';
import ErrorMessage from '../../components/UI/ErrorMessage';
import CampaignSideNav from '../../components/Campaign/CampaignSideNav';
import { useAuth } from '../../contexts/AuthContext';
import { formatApiErrorMessage } from '../../utils/formatApiError';
import { CAMPAIGN_TIMEZONE_OPTIONS } from '../../constants/campaignTimezones';

const createCampaignSchema = z.object({
  name: z.string().min(1, 'Campaign name is required').max(100, 'Name must be less than 100 characters'),
  callDescription: z.string().min(1, 'Call description is required').max(500, 'Description must be less than 500 characters'),
  campaignType: z.enum(['bot', 'human', 'text']).default('human'),
  botEngine: z.enum(BOT_ENGINES).default('telnyx_assistant'),
  botPipelineV2: z.boolean().optional().default(false),
  telnyxAssistantId: z.string().optional(),
  dialingType: z.enum(['auto', 'manual']).default('auto'),
  agentIds: z.array(z.string()).optional().default([]),
  phoneNumberIds: z.array(z.string()).optional().default([]),
  textTemplateBody: z.string().optional(),
  warmupStartVolume: z.number().min(1).max(5000).optional(),
  warmupEndVolume: z.number().min(1).max(10000).optional(),
  warmupDays: z.number().min(1).max(90).optional(),
  quietHoursStart: z.string().optional(),
  quietHoursEnd: z.string().optional(),
  sendSchedules: z.array(z.object({
    startTime: z.string(),
    endTime: z.string(),
    timezone: z.string(),
  })).optional().default([]),
  voiceDialSchedules: z.array(z.object({
    startTime: z.string(),
    endTime: z.string(),
    timezone: z.string(),
  })).optional().default([]),
  sequenceSteps: z.array(z.object({
    stepNumber: z.number().min(1),
    templateBody: z.string(),   // item-level validation handled in superRefine for text campaigns only
    delayHours: z.number().min(0).max(720),
  })).optional(),
  eligibleLeadStatuses: z.array(z.string()).optional().default([]),
  scriptId: z
    .union([z.string().uuid(), z.literal(''), z.null()])
    .optional()
    .transform((value) => (value === '' ? null : value)),
  promptScript: z.string().optional(),
  interestedQuestions: z.string().optional(),
  // Assistant configuration
  assistantFirstMessage: z.string().optional(),
  assistantFirstMessageMode: z
    .enum(['assistant-speaks-first', 'assistant-waits-for-user'])
    .optional()
    .default('assistant-speaks-first'),
  // Conversation parameters
  conversationStyle: z.string().optional(),
  personalityType: z.string().optional(),
  persuasionApproach: z.string().optional(),
  conversationStyles: z.array(z.object({
    id: z.string().uuid().optional(),
    name: z.string(),
    conversationStyle: z.enum(['natural', 'professional', 'casual']),
    personalityType: z.enum(['curious', 'helpful', 'friendly']),
    persuasionApproach: z.enum(['gentle', 'consultative', 'direct']),
    transcriber: z.enum(['deepgram', '11labs', 'assembly-ai', 'azure', 'gladia', 'google', 'openai']).optional(),
    isActive: z.boolean(),
    enhancedPromptScript: z.string().min(1, 'Conversation style prompt is required'),
    selectedVoiceId: z
      .string()
      .nullish()
      .transform((val) => {
        const trimmed = String(val || '').trim();
        return trimmed || undefined;
      }),
    voiceConfig: z.union([
      z.string(),
      z.object({}).passthrough(),
      z.null(),
      z.undefined()
    ]).transform((val) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'string') {
        if (val.trim() === '') return undefined;
        return val;
      }
      if (typeof val === 'object') {
        // Convert existing JSON object to string for new format
        return JSON.stringify(val);
      }
      return undefined;
    }).optional()
  })).optional(),
  timezone: z.string().min(1, 'Timezone is required'),
  timezonePrioritizationEnabled: z.boolean().optional().default(false),
  areaCodePriorityEnabled: z.boolean().optional().default(false),
  callsPerAgent: z.union([z.number().int().min(1).max(10), z.nan(), z.null()]).optional().nullable(),
  leadShuffleEnabled: z.boolean().optional().default(false),
  dialTimezoneFilter: z
    .enum(['auto', 'Eastern', 'Central', 'Mountain', 'Pacific', 'Unknown'])
    .optional()
    .default('auto'),
  captureSchema: z.array(z.object({
    name: z.string().min(1, 'Field name is required'),
    label: z.string().min(1, 'Field label is required'),
    type: z.enum(['string', 'number', 'email', 'phone', 'select', 'multiselect', 'boolean']),
    required: z.boolean(),
    placeholder: z.string().optional(),
    options: z.array(z.string()).optional()
  })).optional()
}).superRefine((data, ctx) => {
  if (data.campaignType === 'bot') {
    // Every engine drives its conversation from the script — self_hosted builds
    // its prompt from the same field the relay engine does, so leaving it out
    // here let a campaign be created with nothing for the bot to say.
    const needsScript =
      data.botEngine === 'telnyx_assistant' ||
      data.botEngine === 'conversation_relay' ||
      data.botEngine === 'vapi_direct' ||
      data.botEngine === 'vapi_bridge' ||
      data.botEngine === 'self_hosted';

    if (data.botEngine === 'telnyx_assistant') {
      if (!data.telnyxAssistantId || !data.telnyxAssistantId.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['telnyxAssistantId'], message: 'Telnyx Assistant ID is required for Telnyx AI Assistant' });
      }
    }

    if (needsScript) {
      if (!data.promptScript || data.promptScript.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['promptScript'], message: 'Script is required' });
      }
      if (!data.assistantFirstMessage || data.assistantFirstMessage.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['assistantFirstMessage'], message: 'First Message (Greetings) is required' });
      }
    }

    // self_hosted takes its voice and style prompt from a conversation style
    // too, so without one there is nothing for the call to speak with.
    if (
      data.botEngine === 'conversation_relay' ||
      data.botEngine === 'vapi_direct' ||
      data.botEngine === 'vapi_bridge' ||
      data.botEngine === 'self_hosted'
    ) {
      if (!data.conversationStyles || data.conversationStyles.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['conversationStyles'], message: 'At least one conversation style is required' });
      } else {
        const hasPrompt = data.conversationStyles.some(
          style => style.enhancedPromptScript && style.enhancedPromptScript.trim().length > 0
        );
        if (!hasPrompt) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['conversationStyles'], message: 'At least one conversation style must have a prompt' });
        }
        const missingVoice = data.conversationStyles.some(
          (style) => style.isActive && !(style.selectedVoiceId || '').trim()
        );
        if (missingVoice) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['conversationStyles'],
            message: 'Each active conversation style needs a voice (ElevenLabs or Fish Audio on self-hosted)',
          });
        }
        const voiceProviderError = conversationStylesVoiceError(data.botEngine, data.conversationStyles);
        if (voiceProviderError && !missingVoice) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['conversationStyles'],
            message: voiceProviderError,
          });
        }
      }
    }

    if (data.botEngine === 'vapi_bridge' && (!data.phoneNumberIds || data.phoneNumberIds.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phoneNumberIds'],
        message: 'Select at least one Telnyx outbound number for bridge dialing',
      });
    }
  }
  if (data.campaignType === 'text') {
    if (!data.sequenceSteps || data.sequenceSteps.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sequenceSteps'], message: 'Add at least one message in the sequence' });
    } else {
      data.sequenceSteps.forEach((step, i) => {
        if (!step.templateBody || step.templateBody.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sequenceSteps'],
            message: i === 0 ? 'Message 1 text is required' : `Follow-up ${i} message text is required`,
          });
        }
      });
    }
    if (!data.phoneNumberIds || data.phoneNumberIds.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phoneNumberIds'], message: 'Select at least one SMS-capable number' });
    }
    if (!data.eligibleLeadStatuses || data.eligibleLeadStatuses.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eligibleLeadStatuses'],
        message: 'SMS allow-list required: select at least one lead status that should receive automated SMS.',
      });
    }
    data.sendSchedules?.forEach((slot, index) => {
      if (!slot.startTime?.trim() || !slot.endTime?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sendSchedules'],
          message: `Send window ${index + 1}: start and end time are required`,
        });
      } else if (slot.startTime === slot.endTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sendSchedules'],
          message: `Send window ${index + 1}: start and end time cannot be the same`,
        });
      }
    });
  }
  if (data.campaignType === 'bot' || (data.campaignType === 'human' && data.dialingType === 'auto')) {
    data.voiceDialSchedules?.forEach((slot, index) => {
      if (!slot.startTime?.trim() || !slot.endTime?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['voiceDialSchedules'],
          message: `Dial window ${index + 1}: start and end time are required`,
        });
      } else if (slot.startTime === slot.endTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['voiceDialSchedules'],
          message: `Dial window ${index + 1}: start and end time cannot be the same`,
        });
      }
    });
  }
  if (data.campaignType === 'human') {
    if (!data.agentIds || data.agentIds.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['agentIds'], message: 'At least one agent must be assigned' });
    }
  }
});

type CreateCampaignFormData = z.infer<typeof createCampaignSchema>;

const CreateCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [activeSection, setActiveSection] = useState('basic-info');
  const [agents, setAgents] = useState<AdminUser[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const isUser = user?.role === 'user' || user?.role === 'agent';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors }
  } = useForm<CreateCampaignFormData>({
    resolver: zodResolver(createCampaignSchema),
    defaultValues: {
      campaignType: 'human',
      dialingType: 'auto',
      agentIds: [],
      scriptId: null,
      phoneNumberIds: [],
      timezone: (() => {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const match = CAMPAIGN_TIMEZONE_OPTIONS.find((o) => o.value === browserTz);
        return match?.value ?? 'America/New_York';
      })(),
      timezonePrioritizationEnabled: false,
      areaCodePriorityEnabled: false,
      callsPerAgent: null,
      assistantFirstMessageMode: 'assistant-speaks-first',
      leadShuffleEnabled: false,
      dialTimezoneFilter: 'auto',
      captureSchema: [],
      conversationStyles: [],
      textTemplateBody: '',
      warmupStartVolume: 25,
      warmupEndVolume: 250,
      warmupDays: 14,
      quietHoursStart: '',
      quietHoursEnd: '',
      sendSchedules: [],
      voiceDialSchedules: [],
      sequenceSteps: [
        { stepNumber: 1, templateBody: '', delayHours: 0 },
      ],
      eligibleLeadStatuses: [],
    }
  });

  const captureSchema = watch('captureSchema') || [];
  const sequenceSteps = watch('sequenceSteps') || [];
  const eligibleLeadStatuses = watch('eligibleLeadStatuses') || [];
  const campaignType = watch('campaignType');
  const botEngine = watch('botEngine');
  const isHumanCampaign = campaignType === 'human';
  const isBotCampaign = campaignType === 'bot';
  const isTextCampaign = campaignType === 'text';
  const isTelnyxAssistantBot = isBotCampaign && botEngine === 'telnyx_assistant';
  const isVapiDirectBot = isBotCampaign && botEngine === 'vapi_direct';
  const isVapiBridgeBot = isBotCampaign && botEngine === 'vapi_bridge';
  const isConversationRelayBot = isBotCampaign && botEngine === 'conversation_relay';

  const [phoneLoadError, setPhoneLoadError] = useState<string | null>(null);

  const displayPhoneNumbers = React.useMemo(() => {
    return phoneNumbers.map(p => {
      const localId = (p as PhoneNumber & { localPhoneNumberId?: string }).localPhoneNumberId;
      const agent = agents.find(
        (a) => a.phone_number_id === p.id || (localId && a.phone_number_id === localId)
      );
      if (agent) {
        return {
          ...p,
          name: `${agent.name} (${agent.email}) — ${p.number}`
        };
      }
      return p;
    });
  }, [phoneNumbers, agents]);

  // Telnyx numbers for bridge / SMS pickers (Vapi Direct has no campaign picker)
  useEffect(() => {
    if (isVapiDirectBot) return;
    const loadData = async () => {
      try {
        setLoadingPhoneNumbers(true);
        setPhoneLoadError(null);

        const phoneNums = await campaignService.getPhoneNumbers();
        setPhoneNumbers(phoneNums);
        if (phoneNums.length === 0) {
          setPhoneLoadError('No numbers returned from Telnyx. Check API key / connection in provider settings.');
        }
      } catch (error: any) {
        console.error('Failed to load data:', error);
        const msg =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load Telnyx phone numbers';
        setPhoneLoadError(msg);
        setPhoneNumbers([]);
        toast.error(msg);
      } finally {
        setLoadingPhoneNumbers(false);
      }
    };

    loadData();
  }, [isVapiDirectBot]);

  useEffect(() => {
    const loadAgents = async () => {
      // Always load agents to filter phone numbers
      setLoadingAgents(true);
      try {
        const response = await adminService.getUsers({ role: 'agent', page: 1, limit: 100 });
        const list = response?.data?.users ?? [];
        setAgents(list);
      } catch (error) {
        console.error('Failed to load agents:', error);
        toast.error('Failed to load agents');
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, []); // Run once on mount

  useEffect(() => {
    const loadScripts = async () => {
      setLoadingScripts(true);
      try {
        const data = await scriptService.getScripts();
        setScripts(data.filter((script) => script.isActive));
      } catch {
        toast.error('Failed to load scripts');
      } finally {
        setLoadingScripts(false);
      }
    };

    loadScripts();
  }, []);

  useEffect(() => {
    if (!isBotCampaign && ['conversation-styles', 'script-config', 'data-capture'].includes(activeSection)) {
      setActiveSection('basic-info');
    }
  }, [isBotCampaign, activeSection]);

  useEffect(() => {
    if (!isTextCampaign) return;
    const first = sequenceSteps[0]?.templateBody ?? '';
    setValue('textTemplateBody', first);
  }, [isTextCampaign, sequenceSteps, setValue]);

  const scrollToError = (formErrors: FieldErrors<CreateCampaignFormData>) => {
    // Map each field to its section
    const fieldSectionMap: { field: keyof CreateCampaignFormData; section: string }[] = [
      { field: 'name', section: 'basic-info' },
      { field: 'callDescription', section: 'basic-info' },
      { field: 'phoneNumberIds', section: 'basic-info' },
      { field: 'eligibleLeadStatuses', section: 'basic-info' },
      { field: 'sequenceSteps', section: 'basic-info' },
      { field: 'agentIds', section: 'assign-agents' },
      { field: 'conversationStyles', section: 'conversation-styles' },
      { field: 'promptScript', section: 'script-config' },
      { field: 'assistantFirstMessage', section: 'script-config' },
    ];

    const resolveErrorElement = (field: keyof CreateCampaignFormData): HTMLElement | null => {
      if (field === 'eligibleLeadStatuses') {
        return document.querySelector('[data-field="eligible-lead-statuses"]') as HTMLElement;
      }
      return document.querySelector(`[name="${field}"]`) as HTMLElement;
    };

    for (const { field, section } of fieldSectionMap) {
      if (formErrors[field]) {
        if (section !== activeSection) {
          // Navigate to the correct section first, then scroll
          setActiveSection(section);
          setTimeout(() => {
            const targetElement = resolveErrorElement(field);
            if (targetElement) {
              targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 150);
        } else {
          const targetElement = resolveErrorElement(field);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        break;
      }
    }
  };

  const onSubmit = async (data: CreateCampaignFormData) => {
    console.log('Form submitted with data:', data);

    try {
      setLoading(true);

      // Convert interestedQuestions from string to array
      const interestedQuestions = data.interestedQuestions
        ? data.interestedQuestions.split('\n').map(q => q.trim()).filter(q => q.length > 0)
        : [];

      // Ensure captureSchema is not undefined
      const {
        sendSchedules,
        sequenceSteps: _sequenceSteps,
        textTemplateBody: _textTemplateBody,
        warmupStartVolume,
        warmupEndVolume,
        warmupDays,
        eligibleLeadStatuses,
        voiceDialSchedules,
        ...campaignFields
      } = data;
      const usesVoiceDialWindows =
        data.campaignType === 'bot' ||
        (data.campaignType === 'human' && data.dialingType === 'auto');
      const campaignData = {
        ...campaignFields,
        interestedQuestions,
        captureSchema: data.captureSchema || [],
        quietHoursStart: data.campaignType === 'text' ? undefined : data.quietHoursStart,
        quietHoursEnd: data.campaignType === 'text' ? undefined : data.quietHoursEnd,
        voiceDialSchedules: usesVoiceDialWindows ? voiceDialSchedules || [] : undefined,
        textConfig: data.campaignType === 'text'
          ? {
              templateBody: data.sequenceSteps?.[0]?.templateBody?.trim() || data.textTemplateBody || '',
              warmupStartVolume: warmupStartVolume || 25,
              warmupEndVolume: warmupEndVolume || 250,
              warmupDays: warmupDays || 14,
              eligibleLeadStatuses: eligibleLeadStatuses || [],
              sendSchedules: sendSchedules || [],
            }
          : undefined,
        assistantFirstMessage:
          data.campaignType === 'human'
            ? (data.assistantFirstMessage && data.assistantFirstMessage.trim().length > 0
                ? data.assistantFirstMessage
                : 'This call will be handled by a human agent.')
            : data.campaignType === 'text'
              ? 'SMS campaign entrypoint'
            : (data.assistantFirstMessage ?? ''),
        assistantFirstMessageMode:
          data.campaignType === 'bot'
            ? (data.assistantFirstMessageMode || 'assistant-speaks-first')
            : 'assistant-speaks-first',
        promptScript:
          data.campaignType === 'human'
            ? (data.promptScript && data.promptScript.trim().length > 0
                ? data.promptScript
                : 'Human agent call.')
            : data.campaignType === 'text'
              ? 'SMS campaign'
            : (data.promptScript ?? ''),
        scriptId: data.scriptId || null,
        phoneNumberIds:
          data.campaignType === 'bot' && data.botEngine === 'vapi_direct'
            ? []
            : data.phoneNumberIds || [],
      };

      console.log('Sending campaign data:', campaignData);
      console.log('Conversation styles in campaign data:', campaignData.conversationStyles);
      const campaign = await campaignService.createCampaign(campaignData);
      if (data.campaignType === 'text' && data.sequenceSteps && data.sequenceSteps.length > 0) {
        await campaignService.updateSmsSequenceSteps(campaign.id, data.sequenceSteps);
      }
      console.log('Campaign created successfully:', campaign);
      navigate(`/campaigns/${campaign.id}`);
    } catch (error: any) {
      console.error('Failed to create campaign:', error);

      toast.error(formatApiErrorMessage(error, 'Failed to create campaign'));

      // Scroll to first error field after a short delay
      setTimeout(() => {
        scrollToError(errors);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const onInvalidSubmit = (formErrors: FieldErrors<CreateCampaignFormData>) => {
    // Use a fixed toast id so rapid clicks replace the same toast instead of stacking
    const TOAST_ID = 'campaign-form-error';

    // Log the raw error structure — use a safe serializer to avoid circular ref throws on DOM element refs
    try {
      const safeErrors = JSON.stringify(formErrors, (_k, v) =>
        v instanceof Element || typeof v === 'function' ? '[ref]' : v
      , 2);
      console.warn('[CreateCampaign] validation errors:', safeErrors);
    } catch {
      console.warn('[CreateCampaign] validation errors (raw):', formErrors);
    }

    // Recursively walk the formErrors tree and return the first non-empty message string.
    // Handles all react-hook-form v7 error shapes:
    //   { message }            – plain field
    //   { root: { message } }  – array-level zod error
    //   { [index]: { ... } }   – array item errors
    const extractFirstMessage = (node: any, depth = 0): string | null => {
      if (depth > 5 || node == null) return null;
      if (typeof node === 'string' && node) return node;
      if (typeof node.message === 'string' && node.message) return node.message;
      // RHF stores array min/max errors under .root for Controller-managed arrays
      if (node.root && typeof node.root.message === 'string' && node.root.message) {
        return node.root.message;
      }
      if (typeof node === 'object') {
        for (const val of Object.values(node)) {
          const found = extractFirstMessage(val, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };

    // Walk fields in priority order so the most actionable error surfaces first
    const priorityFields: (keyof CreateCampaignFormData)[] = [
      'name', 'callDescription', 'timezone',
      'agentIds',
      'conversationStyles',
      'promptScript', 'assistantFirstMessage',
      'textTemplateBody', 'phoneNumberIds', 'eligibleLeadStatuses', 'sequenceSteps',
      'captureSchema',
    ];
    let message: string | null = null;
    for (const field of priorityFields) {
      if (formErrors[field]) {
        message = extractFirstMessage(formErrors[field]);
        if (message) break;
      }
    }
    // Fallback: scan remaining keys not in priority list
    if (!message) {
      message = extractFirstMessage(formErrors);
    }

    toast.error(message ?? 'Please fill in all required fields before submitting.', { id: TOAST_ID });

    // Navigate to and scroll to the first error field
    setTimeout(() => {
      scrollToError(formErrors);
    }, 100);
  };

  const addCaptureField = () => {
    const newField: CaptureField = {
      name: '',
      type: 'string',
      required: false,
      label: ''
    };
    setValue('captureSchema', [...captureSchema, newField]);
  };

  const removeCaptureField = (index: number) => {
    const updatedSchema = captureSchema.filter((_, i) => i !== index);
    setValue('captureSchema', updatedSchema);
  };

  const updateCaptureField = (index: number, field: Partial<CaptureField>) => {
    const updatedSchema = captureSchema.map((item, i) =>
      i === index ? { ...item, ...field } : item
    );
    setValue('captureSchema', updatedSchema);
  };

  if (isUser) {
    return (
      <div className="space-y-6">
        <Card className="p-6 mb-8">
          <CardContent className="p-0">
            <h1 className="text-2xl font-bold mb-2 text-foreground">Insufficient Permissions</h1>
            <p className="text-muted-foreground">
              Your account cannot create or edit campaigns or access the script section. If you need elevated access, please contact your administrator.
            </p>
          </CardContent>
        </Card>
        <div className="flex justify-end">
          <Button type="button" onClick={() => navigate('/campaigns')} variant="primary">
            Go to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 mb-8">
        <CardContent className="p-0">
          <h1 className="text-2xl font-bold mb-2 text-foreground">Create New Campaign</h1>
          <p className="text-muted-foreground">
            {isTextCampaign
              ? 'Configure automated SMS sequences and two-way inbox replies.'
              : 'Set up a new calling campaign to reach your leads.'}
          </p>
        </CardContent>
      </Card>

      <div className="flex gap-8">
        <div className="w-80 flex-shrink-0">
          <CampaignSideNav
            sections={[
              {
                id: 'basic-info',
                title: isTextCampaign ? 'SMS campaign setup' : 'Basic Information',
                icon: isTextCampaign ? <ChatBubbleLeftRightIcon className="h-4 w-4" /> : <DocumentTextIcon className="h-4 w-4" />,
              },
              {
                id: 'assign-agents',
                title: isBotCampaign
                  ? 'Assign Agents (pipeline)'
                  : isTextCampaign
                    ? 'Inbox agents (optional)'
                    : 'Assign Agents',
                icon: <UserGroupIcon className="h-4 w-4" />,
              },
              ...(isBotCampaign
                ? [
                    { id: 'script-config', title: 'Script Configuration', icon: <CogIcon className="h-4 w-4" /> },
                    { id: 'conversation-styles', title: 'Conversation Styles', icon: <ChatBubbleLeftRightIcon className="h-4 w-4" /> },
                    { id: 'data-capture', title: 'Data Capture Schema', icon: <TableCellsIcon className="h-4 w-4" /> }
                  ]
                : [])
            ]}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            errorSections={[
              ...(errors.name || errors.callDescription || errors.phoneNumberIds || errors.sequenceSteps ? ['basic-info'] : []),
              ...(!isBotCampaign && errors.agentIds ? ['assign-agents'] : []),
              ...(errors.promptScript || errors.assistantFirstMessage ? ['script-config'] : []),
              ...(errors.conversationStyles ? ['conversation-styles'] : []),
            ]}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">

          <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-8">
            {/* Basic Information */}
            {activeSection === 'basic-info' && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader title="Basic Information" />

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
                        Campaign Name *
                      </label>
                      <input
                        type="text"
                        {...register('name')}
                        className="input"
                        placeholder="Enter campaign name"
                      />
                      <ErrorMessage message={errors.name?.message} />
                    </div>

                    {!isTextCampaign && (
                      <div>
                        <label htmlFor="timezone" className="block text-sm font-semibold text-foreground mb-2">
                          Timezone *
                        </label>
                        <select
                          {...register('timezone')}
                          className="select"
                        >
                          {CAMPAIGN_TIMEZONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage message={errors.timezone?.message} />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Campaign Type</label>
                      <select {...register('campaignType')} className="select">
                        <option value="human">Human Calling</option>
                        <option value="bot">AI Calling</option>
                        <option value="text">SMS / Text</option>
                      </select>
                      {isTextCampaign && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Automated text sequences — not linked to the voice dialer.
                        </p>
                      )}
                    </div>

                    {/* Dialing Type selector — human campaigns only; bot uses auto-dial */}
                    {!isTextCampaign && !isBotCampaign && <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Dialing Mode
                      </label>
                      <div className="flex gap-3">
                        <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          watch('dialingType') === 'auto'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/30'
                        }`}>
                          <input
                            type="radio"
                            value="auto"
                            {...register('dialingType')}
                            className="accent-primary"
                          />
                          <div>
                            <div className="font-medium text-sm">Auto Dialer</div>
                            <div className="text-xs text-muted-foreground">System automatically dials leads</div>
                          </div>
                        </label>
                        <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          watch('dialingType') === 'manual'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/30'
                        }`}>
                          <input
                            type="radio"
                            value="manual"
                            {...register('dialingType')}
                            className="accent-primary"
                          />
                          <div>
                            <div className="font-medium text-sm">Manual Dial</div>
                            <div className="text-xs text-muted-foreground">Agents choose which leads to call</div>
                          </div>
                        </label>
                      </div>
                    </div>}

                    {isBotCampaign && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        AI campaigns automatically dial leads from the queue — no agent required.
                      </p>
                    )}

                    {isTextCampaign && (
                      <SmsCampaignSetupFields
                        sequenceSteps={sequenceSteps}
                        onSequenceStepsChange={(steps) => setValue('sequenceSteps', steps)}
                        onFirstStepMessageChange={(body) => setValue('textTemplateBody', body)}
                        phoneNumberIds={watch('phoneNumberIds') || []}
                        onPhoneNumberIdsChange={(ids) => setValue('phoneNumberIds', ids)}
                        displayPhoneNumbers={displayPhoneNumbers}
                        loadingPhoneNumbers={loadingPhoneNumbers}
                        phoneNumberError={errors.phoneNumberIds?.message as string | undefined}
                        phoneNumbersEmptyHint={phoneLoadError || undefined}
                        sequenceStepsError={errors.sequenceSteps?.message as string | undefined}
                        warmupStartVolume={watch('warmupStartVolume') ?? 25}
                        warmupEndVolume={watch('warmupEndVolume') ?? 250}
                        warmupDays={watch('warmupDays') ?? 14}
                        onWarmupChange={(field, value) => setValue(field, value)}
                        sendSchedules={watch('sendSchedules') || []}
                        onSendSchedulesChange={(schedules) => setValue('sendSchedules', schedules)}
                        sendSchedulesError={errors.sendSchedules?.message as string | undefined}
                        eligibleLeadStatuses={eligibleLeadStatuses}
                        onEligibleLeadStatusesChange={(statuses) => setValue('eligibleLeadStatuses', statuses)}
                        eligibleLeadStatusesError={errors.eligibleLeadStatuses?.message as string | undefined}
                      />
                    )}
                  </div>

                  {!isTextCampaign && (
                    <div className="mt-6 space-y-3">
                      <Controller
                        name="timezonePrioritizationEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={!!field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            label="Enable timezone-based calling window"
                            description="Only surface callable leads for the active ET timezone window."
                          />
                        )}
                      />
                      <Controller
                        name="areaCodePriorityEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={!!field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            label="Prioritize leads by area-code pickup performance"
                            description="Sort eligible leads by highest pickup rate first."
                          />
                        )}
                      />
                      {watch('dialingType') === 'auto' && (
                        <>
                          <div>
                            <label htmlFor="callsPerAgent" className="block text-sm font-semibold text-foreground mb-2">
                              Concurrent lines per agent
                            </label>
                            <input
                              id="callsPerAgent"
                              type="number"
                              min={1}
                              max={10}
                              {...register('callsPerAgent', {
                                setValueAs: (v) => (v === '' || v === null || Number.isNaN(Number(v)) ? null : Number(v)),
                              })}
                              className="input max-w-[120px]"
                              placeholder="3"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                              Leave empty to use the server default (3 lines per agent).
                            </p>
                          </div>
                          <div>
                            <label htmlFor="dialTimezoneFilter" className="block text-sm font-semibold text-foreground mb-2">
                              Lead timezone filter
                            </label>
                            <select id="dialTimezoneFilter" {...register('dialTimezoneFilter')} className="select max-w-xs">
                              <option value="auto">Auto (ET wave or campaign timezone)</option>
                              <option value="Eastern">Eastern</option>
                              <option value="Central">Central</option>
                              <option value="Mountain">Mountain</option>
                              <option value="Pacific">Pacific</option>
                              <option value="Unknown">Unknown</option>
                            </select>
                          </div>
                          <Controller
                            name="leadShuffleEnabled"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                checked={!!field.value}
                                onChange={(event) => field.onChange(event.target.checked)}
                                label="Shuffle lead dial order"
                                description="Randomize eligible leads each scheduler cycle instead of fixed sort order."
                              />
                            )}
                          />
                        </>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div>
                          <label htmlFor="quietHoursStart" className="block text-sm font-semibold text-foreground mb-2">
                            Quiet hours start
                          </label>
                          <input id="quietHoursStart" type="time" {...register('quietHoursStart')} className="input" />
                        </div>
                        <div>
                          <label htmlFor="quietHoursEnd" className="block text-sm font-semibold text-foreground mb-2">
                            Quiet hours end
                          </label>
                          <input id="quietHoursEnd" type="time" {...register('quietHoursEnd')} className="input" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Auto dialer pauses during quiet hours in the campaign timezone. Leave blank to dial anytime.
                      </p>
                      {(isBotCampaign || (isHumanCampaign && watch('dialingType') === 'auto')) && (
                        <div className="pt-4">
                          <DialScheduleFields
                            schedules={watch('voiceDialSchedules') || []}
                            onChange={(schedules) => setValue('voiceDialSchedules', schedules)}
                            error={errors.voiceDialSchedules?.message as string | undefined}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6">
                    <label htmlFor="callDescription" className="block text-sm font-semibold text-foreground mb-2">
                      {isTextCampaign ? 'Campaign notes (internal) *' : 'Call Description *'}
                    </label>
                    <textarea
                      {...register('callDescription')}
                      rows={3}
                      className="textarea"
                      placeholder={
                        isTextCampaign
                          ? 'e.g. Q2 re-engagement SMS for inbound form leads'
                          : 'Describe what this campaign is about and its purpose'
                      }
                    />
                    {isTextCampaign && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        For your team only — not sent to leads as an SMS.
                      </p>
                    )}
                    <ErrorMessage message={errors.callDescription?.message} />
                  </div>

                  {!isTextCampaign && (
                    <div className="mt-6">
                      <label htmlFor="scriptId" className="block text-sm font-semibold text-foreground mb-2">
                        Manual Dial Script
                      </label>
                      <select
                        id="scriptId"
                        {...register('scriptId')}
                        className="select"
                        disabled={loadingScripts}
                      >
                        <option value="">{loadingScripts ? 'Loading scripts...' : 'No script assigned'}</option>
                        {scripts.map((script) => (
                          <option key={script.id} value={script.id}>
                            {script.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-muted-foreground">
                        The selected script appears in the agent in-call popup for manual dialing.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Assign Agents (human dialing + AI pipeline / SMS inbox) */}
            {activeSection === 'assign-agents' && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader
                    title={
                      isBotCampaign
                        ? 'Assign Agents (pipeline)'
                        : isTextCampaign
                          ? 'Inbox agents (optional)'
                          : 'Assign Agents'
                    }
                  />

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Select Agents{isTextCampaign || isBotCampaign ? '' : ' *'}
                      </label>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {isBotCampaign
                          ? 'Optional: choose agents who get pipeline leads (Interested, Follow Up, App In, App Out) and live Bot Monitor access for this AI campaign.'
                          : isTextCampaign
                            ? 'Optional: assign agents who can reply in the SMS inbox. Voice dialer is not used for text campaigns.'
                            : 'Choose which agents will handle calls for this campaign. At least one agent must be assigned.'}
                      </p>

                      {loadingAgents ? (
                        <div className="mt-3 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          <span className="ml-2 text-sm text-muted-foreground">Loading agents...</span>
                        </div>
                      ) : agents.length === 0 ? (
                        <div className="mt-3 p-4 bg-muted rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground">No agents available. Please create agents first in the Admin panel.</p>
                        </div>
                      ) : (
                        <Controller
                          name="agentIds"
                          control={control}
                          render={({ field }) => {
                            const selectedIds = field.value || [];
                            const filteredAgents = agents.filter(a =>
                              (a.name || '').toLowerCase().includes(agentSearch.toLowerCase()) ||
                              a.email.toLowerCase().includes(agentSearch.toLowerCase())
                            );
                            const allSelected = agents.length > 0 && selectedIds.length === agents.length;
                            const someSelected = selectedIds.length > 0 && selectedIds.length < agents.length;

                            const handleToggleAgent = (agentId: string) => {
                              if (selectedIds.includes(agentId)) {
                                field.onChange(selectedIds.filter((id: string) => id !== agentId));
                              } else {
                                field.onChange([...selectedIds, agentId]);
                              }
                            };

                            const handleSelectAll = () => {
                              if (allSelected) {
                                field.onChange([]);
                              } else {
                                field.onChange(agents.map(a => a.id));
                              }
                            };

                            return (
                              <div className="mt-2">
                                {/* Select All Header */}
                                <div className="flex items-center justify-between p-3 bg-muted border border-border rounded-t-lg">
                                  <label className="flex items-center cursor-pointer">
                                    <div className="relative">
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(input) => {
                                          if (input) input.indeterminate = someSelected;
                                        }}
                                        onChange={handleSelectAll}
                                        className="sr-only"
                                      />
                                      <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                                        allSelected
                                          ? 'bg-primary border-primary shadow-sm'
                                          : someSelected
                                          ? 'bg-primary border-primary shadow-sm'
                                          : 'border-border hover:border-primary/60 bg-card'
                                      }`}>
                                        {allSelected && (
                                          <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {someSelected && !allSelected && (
                                          <div className="w-2 h-2 bg-primary-foreground rounded-sm"></div>
                                        )}
                                      </div>
                                    </div>
                                    <span className="ml-3 text-sm font-medium text-foreground">
                                      {allSelected ? 'Unselect All' : 'Select All'}
                                    </span>
                                  </label>
                                  <span className="text-xs text-muted-foreground">
                                    {selectedIds.length} of {agents.length} selected
                                  </span>
                                </div>

                                {/* Search Agents */}
                                <div className="px-3 py-2 border border-t-0 border-border bg-card">
                                  <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <input
                                      type="text"
                                      placeholder="Search agents by name or email..."
                                      value={agentSearch}
                                      onChange={(e) => setAgentSearch(e.target.value)}
                                      className="input pl-8 py-1.5 text-sm w-full"
                                    />
                                  </div>
                                </div>

                                {/* Agent List */}
                                <div className="border border-t-0 border-border rounded-b-lg max-h-64 overflow-y-auto">
                                  {filteredAgents.length === 0 ? (
                                    <div className="p-4 text-center text-sm text-muted-foreground">
                                      No agents match "{agentSearch}"
                                    </div>
                                  ) : filteredAgents.map((agent, index) => {
                                    const isSelected = selectedIds.includes(agent.id);
                                    return (
                                      <div
                                        key={agent.id}
                                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors duration-150 ${
                                          isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                                        } ${index < filteredAgents.length - 1 ? 'border-b border-border' : ''}`}
                                        onClick={() => handleToggleAgent(agent.id)}
                                      >
                                        <div className="flex items-center min-w-0 flex-1">
                                          <div className="relative">
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => handleToggleAgent(agent.id)}
                                              className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                                              isSelected
                                                ? 'bg-primary border-primary shadow-sm'
                                                : 'border-border hover:border-primary/60 bg-card'
                                            }`}>
                                              {isSelected && (
                                                <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                            </div>
                                          </div>
                                          <div className="ml-3 min-w-0">
                                            <p className={`text-sm font-medium truncate ${
                                              isSelected ? 'text-foreground' : 'text-muted-foreground'
                                            }`}>
                                              {agent.name || agent.email}
                                            </p>
                                            {agent.name && (
                                              <p className="text-xs text-muted-foreground truncate">
                                                {agent.email}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="ml-2 flex-shrink-0">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            agent.phone_number_provider
                                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                          }`}>
                                            {agent.phone_number_provider ? 'WebRTC Ready' : 'No WebRTC'}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }}
                        />
                      )}
                      <ErrorMessage message={errors.agentIds?.message} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Conversation Parameters */}
            {isBotCampaign && activeSection === 'conversation-styles' && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader title="Conversation Styles" className="mb-4" />
                  <p className="text-sm text-muted-foreground mb-6">
                    Set Bot Engine in Script Configuration first — then pick the matching voice here. The AI randomly uses one active style per call.
                  </p>

                  <Controller
                    name="conversationStyles"
                    control={control}
                    render={({ field }) => (
                      <ConversationStylesManager
                        conversationStyles={field.value || []}
                        onChange={field.onChange}
                        onGeneratingChange={setIsGeneratingPrompt}
                        botEngine={botEngine}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            )}

            {/* Script Configuration */}
            {isBotCampaign && activeSection === 'script-config' && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader title="Script Configuration" />

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">Bot Engine</label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Choose engine first — Conversation Styles voices follow this (Telnyx NaturalHD, ElevenLabs, or Fish Audio on self-hosted).
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" value="telnyx_assistant" {...register('botEngine')} className="mt-1" />
                          <span>
                            <span className="font-medium">Telnyx AI Assistant</span>
                            <span className="ml-2 text-xs text-violet-600">Recommended</span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" value="conversation_relay" {...register('botEngine')} className="mt-1" />
                          <span className="font-medium">Custom AI (OpenAI + Telnyx)</span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" value="vapi_direct" {...register('botEngine')} className="mt-1" />
                          <span>
                            <span className="font-medium">VAPI Direct</span>
                            <span className="block text-xs text-muted-foreground">VAPI dials via Telnyx numbers imported in VAPI (~$0.15–0.30/min)</span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" value="vapi_bridge" {...register('botEngine')} className="mt-1" />
                          <span>
                            <span className="font-medium">VAPI Bridge</span>
                            <span className="block text-xs text-muted-foreground">Telnyx dial + connect to VAPI on answer (~$0.28–0.45+/min)</span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" value="self_hosted" {...register('botEngine')} className="mt-1" />
                          <span>
                            <span className="font-medium">Full Custom AI (self-hosted)</span>
                            <span className="ml-2 text-xs text-amber-600">Experimental</span>
                            <span className="block text-xs text-muted-foreground">
                              Our own STT + TTS over Telnyx media streaming — no Conversation Relay fee.
                              Full conversations are implemented but have not yet run on a live call.
                              Behaviour follows the server&apos;s SELF_HOSTED_BOT_MODE (smoke / listen / full).
                              Test on your own number first.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>

                    {isConversationRelayBot && (
                      <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('botPipelineV2')}
                            className="mt-1 h-4 w-4 rounded border-border"
                          />
                          <span>
                            <span className="font-medium text-foreground">Human voice pipeline (v2)</span>
                            <span className="block text-xs text-muted-foreground mt-1">
                              ElevenLabs + Deepgram + stage engine. Enable after platform ElevenLabs secret is set on Telnyx.
                            </span>
                          </span>
                        </label>
                      </div>
                    )}

                    {isVapiDirectBot && (
                      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                        Outbound caller IDs are chosen automatically from{' '}
                        <span className="font-medium text-foreground">Manage Numbers</span>
                        {' '}— active numbers with purpose <span className="font-medium text-foreground">AI Caller</span>
                        {' '}that are pushed to Vapi. No per-campaign selection needed.
                      </div>
                    )}

                    {isVapiBridgeBot && (
                      <div data-field="phone-numbers">
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          Telnyx Outbound Numbers
                        </label>
                        <Controller
                          name="phoneNumberIds"
                          control={control}
                          render={({ field }) => (
                            <PhoneNumberList
                              phoneNumbers={displayPhoneNumbers}
                              selectedPhoneNumberIds={field.value || []}
                              onSelectionChange={field.onChange}
                              loading={loadingPhoneNumbers}
                              error={errors.phoneNumberIds?.message}
                            />
                          )}
                        />
                      </div>
                    )}

                    {isTelnyxAssistantBot && (
                      <Controller
                        name="telnyxAssistantId"
                        control={control}
                        render={({ field }) => (
                          <TelnyxAssistantPicker
                            inputId="telnyxAssistantId"
                            value={field.value || ''}
                            onChange={field.onChange}
                            error={errors.telnyxAssistantId?.message}
                          />
                        )}
                      />
                    )}

                    {/* First Message Override */}
                    <div>
                      <label htmlFor="assistantFirstMessage" className="block text-sm font-semibold text-foreground mb-2">
                        First Message (Greetings) *
                      </label>
                      <textarea
                        {...register('assistantFirstMessage')}
                        rows={3}
                        className="textarea"
                        placeholder="Enter the greeting message for customers when the call starts..."
                      />
                      <ErrorMessage message={errors.assistantFirstMessage?.message} />
                      <p className="mt-2 text-sm text-muted-foreground">
                        This message will be used to greet the customer when the call starts.
                      </p>
                      <PlaceholderHelper className="mt-3" />
                    </div>

                    {watch('campaignType') === 'bot' && (
                      <div>
                        <label htmlFor="assistantFirstMessageMode" className="block text-sm font-semibold text-foreground mb-2">
                          Who speaks first
                        </label>
                        <select
                          id="assistantFirstMessageMode"
                          {...register('assistantFirstMessageMode')}
                          className="input"
                        >
                          <option value="assistant-speaks-first">AI greets first</option>
                          <option value="assistant-waits-for-user">Wait for client to speak first</option>
                        </select>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Use wait mode to test whether the client saying “hello” first improves the conversation.
                        </p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="promptScript" className="block text-sm font-medium text-foreground">
                        Main Script *
                      </label>
                      <textarea
                        {...register('promptScript')}
                        rows={8}
                        className="textarea"
                        placeholder="Enter the script that the AI will use during calls..."
                      />
                      <ErrorMessage message={errors.promptScript?.message} />
                      <PlaceholderHelper className="mt-3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data Capture Schema */}
            {isBotCampaign && activeSection === 'data-capture' && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <SectionHeader title="Data Capture Schema" />
                      <p className="mt-1 text-sm text-muted-foreground">
                        Define what information to collect from interested leads
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={addCaptureField}
                      variant="outlined"
                      size="sm"
                    >
                      <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      Add Field
                    </Button>
                  </div>

                  {captureSchema.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                      <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <InformationCircleIcon className="h-6 w-6 text-primary" />
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        No capture fields defined. Add fields to collect specific information from interested leads.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {captureSchema.map((field, index) => (
                        <div key={index} className="border border-border rounded-lg p-4">
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                              <div>
                                <label className="block text-sm font-medium text-foreground">
                                  Field Name *
                                </label>
                                <input
                                  type="text"
                                  value={field.name}
                                  onChange={(e) => updateCaptureField(index, { name: e.target.value })}
                                  className="input"
                                  placeholder="e.g., company_name"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-foreground">
                                  Field Label *
                                </label>
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) => updateCaptureField(index, { label: e.target.value })}
                                  className="input"
                                  placeholder="e.g., Company Name"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-foreground">
                                  Field Type *
                                </label>
                                <select
                                  value={field.type}
                                  onChange={(e) => updateCaptureField(index, { type: e.target.value as CaptureField['type'] })}
                                  className="select"
                                >
                                  <option value="string">Text</option>
                                  <option value="email">Email</option>
                                  <option value="number">Number</option>
                                  <option value="boolean">Yes/No</option>
                                </select>
                              </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="switch-group">
                                <Switch
                                  checked={field.required}
                                  onChange={(event) => updateCaptureField(index, { required: event.target.checked })}
                                  label="Required"
                                />
                              </div>

                              <Button
                                type="button"
                                onClick={() => removeCaptureField(index)}
                                variant="destructive"
                                size="sm"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-foreground">
                              Description
                            </label>
                            <input
                              type="text"
                              value={field.placeholder || ''}
                              onChange={(e) => updateCaptureField(index, { placeholder: e.target.value })}
                              className="input"
                              placeholder="Optional description for this field"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => navigate('/campaigns')}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={
                  loading ||
                  isGeneratingPrompt ||
                  (isTextCampaign && displayPhoneNumbers.length === 0)
                }
              >
                Create Campaign
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignPage;
